<template>
  <div
    ref="navRoot"
    v-if="!isOnePlanAvailable"
    :class="[
      'shop-section-sticky-nav-bar',
      stickyBar && appReady && 'shop-section-sticky-nav-bar--z-index',
      stickyBar && isHidden && 'shop-section-sticky-nav-bar--hidden',
      stickyBar && !isHidden && 'shop-section-sticky-nav-bar--show',
      stickyBar && 'shop-section-sticky-nav-bar--sticky-bar',
    ]"
    :data-variant="stickyBar ? 'sticky' : 'default'"
  >
    <div class="shop-section-sticky-nav-bar__wrapper">
      <SubscriptionsViewToggler v-if="displayViewToggler" />
      <ShopSectionNav :isStickyBar="stickyBar && !isHidden" :defaultPill="defaultPill" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import SubscriptionsViewToggler from '@/components/flow/SubscriptionsViewToggler.vue'
import ShopSectionNav from '@/components/flow/ShopSectionNav.vue'

export default {
  name: 'ShopSectionStickyNavBar',
  components: {
    SubscriptionsViewToggler,
    ShopSectionNav,
  },
  props: {
    stickyBar: {
      type: Boolean,
      default: false,
    },
    defaultPill: {
      type: String,
      default: '',
    },
    customShopContainerName: {
      type: String,
      required: false,
      default: 'shop__content',
    },
  },
  data() {
    return {
      isHidden: true,
      appReady: false,
      resizeListener: null,
    }
  },
  computed: {
    ...mapState({
      isOnePlanAvailable: state => state.products.isOnePlanAvailable,
    }),
    ...mapGetters({
      usersCurrentSubscriptions: 'subscriptions/getUsersCurrentSubscriptions',
      isBusinessUser: 'business/isBusinessUser',
    }),
    userHasSubscription() {
      return this.usersCurrentSubscriptions.length > 0
    },
    displayViewToggler() {
      if (this.isBusinessUser) {
        return false
      }
      return !this.userHasSubscription
    },
  },
  mounted() {
    window.addEventListener('scroll', this.checkScroll)
    if (this.stickyBar) {
      this.resizeListener = () => {
        // Recompute visibility on resize to match new layout offsets
        this.checkScroll()
      }
      window.addEventListener('resize', this.resizeListener)
    }
    // needed to set isHidden initial state
    this.checkScroll()
    // This is a hack to prevent the sticky bar from showing on page load
    setTimeout(() => {
      this.appReady = true
    }, 1000)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.checkScroll)
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener)
      this.resizeListener = null
    }
  },
  methods: {
    getContainerElement() {
      const containerClass = this.customShopContainerName || 'shop__content'
      return document.querySelector(`.${containerClass}`)
    },
    getDefaultNavElement(container) {
      if (!container || typeof container.querySelector !== 'function') {
        return null
      }
      return container.querySelector('.shop-section-sticky-nav-bar[data-variant="default"]')
    },
    checkScroll() {
      if (this.stickyBar) {
        const container = this.getContainerElement()
        const defaultNav = this.getDefaultNavElement(container)

        if (defaultNav) {
          const { top } = defaultNav.getBoundingClientRect()
          const navHeight = defaultNav.offsetHeight || 0
          const distanceBeforeOverlap = top + window.scrollY - navHeight
          this.isHidden = window.scrollY <= distanceBeforeOverlap
          return
        }

        if (container) {
          const threshold = container.offsetTop - 20
          this.isHidden = window.scrollY <= threshold
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.shop-section-sticky-nav-bar {
  $this: &;
  &__wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-flow: column;
    gap: 10px;
    transition: all 0.1s ease-in-out;
    z-index: -1;
    @include mobile {
      gap: 15px;
    }
    @include tablet {
      flex-flow: row;
    }
  }
  &--z-index {
    z-index: 3;
  }
  &--hidden {
    display: none;
  }
  &--show {
    opacity: 1;
  }
  &--sticky-bar {
    position: sticky;
    top: 0;
    width: 100%;
    background: linear-gradient(180deg, #1e1e1e 71.82%, rgba(30, 30, 30, 0) 100%);
    padding: 10px;
    @include mobile {
      padding: 20px;
    }
    #{$this} {
      &__wrapper {
        margin: auto;
        max-width: 1120px;
      }
    }
  }
}
</style>
